import React, { ReactNode } from 'react';

import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AuthCallbackHandler from '~/components/Auth/AuthCallbackHandler';
import AuthWrapper from '~/components/Auth/AuthWrapper';
import Chat from '~/components/Chat/Chat';
import Claim from '~/components/Claim/Claim';
import ClaimPageBodyWrapper from '~/components/Claim/ClaimPageBodyWrapper';
import Fnol from '~/components/Fnol';
import Home from '~/components/Home';
import ProtectedLayout from '~/components/Layout/ProtectedLayout';
import { AuthContextProvider, useAuth } from '~/context/AuthContext';
import { MainTopBarProvider } from '~/context/MainTopBarContext';
import { RealTimeUpdatesTaskProvider } from '~/context/RealTimeUpdatesContext';
import { Spinner } from '~/core/atoms/Spinner';
import './App.css';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoadingUser } = useAuth();
  const location = useLocation();

  if (isLoadingUser) {
    return (
      <div className="flex min-h-screen items-center justify-center text-white">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Store the current location in state to redirect back after authentication
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const publicRoutes = {
  auth: {
    path: '/auth',
    element: <AuthWrapper />,
  },
  authCallback: {
    path: '/auth/callback',
    element: <AuthCallbackHandler />,
  },
};

const AppRoutes: React.FC = () => (
  <div className="bg-gradient-1 h-screen w-screen bg-cover">
    <AuthContextProvider>
      <MainTopBarProvider>
        <RealTimeUpdatesTaskProvider>
          <Routes>
            {/* Public routes */}
            {Object.entries(publicRoutes).map(([key, { path, element }]) => (
              <Route key={key} path={path} element={element} />
            ))}
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ProtectedLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Home />} />
              <Route path="fnol" element={<Fnol />} />
              <Route path="claims/:claimNumber" element={<Claim />}>
                <Route index element={<ClaimPageBodyWrapper />} />
                <Route path="chat/:chatId" element={<Chat />} />
              </Route>
            </Route>
            {/* Redirect all other routes to the main route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </RealTimeUpdatesTaskProvider>
      </MainTopBarProvider>
    </AuthContextProvider>
  </div>
);

const App: React.FC = () => (
  <Router>
    <AppRoutes />
  </Router>
);

export default App;
